import React, { useEffect, useState } from 'react';
import { TrendingUp, DollarSign, Clock, AlertTriangle, X, Zap, Package, Tag, Percent, Gift, Star, ChevronRight, ExternalLink, Info } from 'lucide-react';

const ImprovedPremiumProductSection = ({ product, onInvest }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeStage, setActiveStage] = useState(1);
  const [isVideoLoading, setIsVideoLoading] = useState(true);
  const [showDiscount, setShowDiscount] = useState(false);

  // Calculate original price (current price + 10%)
  const discountPercent = 10;
  const originalPrice = Math.round(product?.price / 0.9);
  const savings = originalPrice - product?.price;

  useEffect(() => {
    setIsVisible(true);
    
    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0.1 }
    );

    const element = document.querySelector('.improved-premium-product-visiontech-container');
    if (element) observer.observe(element);

    // Animate discount tag after initial load
    setTimeout(() => {
      setShowDiscount(true);
    }, 1000);

    return () => observer.disconnect();
  }, []);

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
    if (!isModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  };

  const handleVideoLoaded = () => {
    setIsVideoLoading(false);
  };

  useEffect(() => {
    // Reset video loading state when stage changes
    setIsVideoLoading(true);
  }, [activeStage]);

  if (!product) return null;

  return (
    <>
      <div className={`improved-premium-product-visiontech-container ${isVisible ? 'visible' : ''}`}>
        <div className="improved-premium-product-visiontech-particles">
          <div className="improved-premium-product-visiontech-particle p1"></div>
          <div className="improved-premium-product-visiontech-particle p2"></div>
          <div className="improved-premium-product-visiontech-particle p3"></div>
          <div className="improved-premium-product-visiontech-particle p4"></div>
        </div>
        
        <div className="improved-premium-product-visiontech-content">
          <div className="improved-premium-product-visiontech-header">
            <div className="improved-premium-product-visiontech-badge-area">
              <span className="improved-premium-product-visiontech-premium-badge">
                <Star size={12} strokeWidth={3} />
                <span>PREMIUM</span>
              </span>
              
              <span className={`improved-premium-product-visiontech-discount-badge ${showDiscount ? 'show' : ''}`}>
                <Percent size={12} />
                <span>{discountPercent}% OFF</span>
              </span>
            </div>
            
            <h2 className="improved-premium-product-visiontech-title">{product.name}</h2>
          </div>
          
          <div className="improved-premium-product-visiontech-main-area">
            <div className="improved-premium-product-visiontech-image-wrapper">
              <div className="improved-premium-product-visiontech-image-glow"></div>
              <img 
                src={`https://api.visiontech2030.com${product.imageUrl}`}
                alt={product.name}
                className="improved-premium-product-visiontech-image"
              />
              <div className="improved-premium-product-visiontech-image-overlay">
                <Gift size={14} />
                <span>Limited Offer</span>
              </div>
            </div>
            
            <div className="improved-premium-product-visiontech-details">
              <div className="improved-premium-product-visiontech-stat-row">
                <div className="improved-premium-product-visiontech-stat">
                  <DollarSign size={14} className="improved-premium-product-visiontech-stat-icon" />
                  <div className="improved-premium-product-visiontech-stat-content">
                    <span className="improved-premium-product-visiontech-stat-label">Daily Earnings</span>
                    <span className="improved-premium-product-visiontech-stat-value">KES {product.dailyIncome}</span>
                  </div>
                </div>
                
                <div className="improved-premium-product-visiontech-stat">
                  <Clock size={14} className="improved-premium-product-visiontech-stat-icon" />
                  <div className="improved-premium-product-visiontech-stat-content">
                    <span className="improved-premium-product-visiontech-stat-label">Cycle Period</span>
                    <span className="improved-premium-product-visiontech-stat-value">{product.cycle} days</span>
                  </div>
                </div>
              </div>
              
              <div className="improved-premium-product-visiontech-stat-row">
                <div className="improved-premium-product-visiontech-stat">
                  <TrendingUp size={14} className="improved-premium-product-visiontech-stat-icon" />
                  <div className="improved-premium-product-visiontech-stat-content">
                    <span className="improved-premium-product-visiontech-stat-label">Total Return</span>
                    <span className="improved-premium-product-visiontech-stat-value highlight">KES {product.totalIncome}</span>
                  </div>
                </div>
                
                <div className="improved-premium-product-visiontech-stat">
                  <Tag size={14} className="improved-premium-product-visiontech-stat-icon" />
                  <div className="improved-premium-product-visiontech-stat-content">
                    <span className="improved-premium-product-visiontech-stat-label">Share Price</span>
                    <div className="improved-premium-product-visiontech-price-display">
                      <span className="improved-premium-product-visiontech-original-price">KES {originalPrice}</span>
                      <span className="improved-premium-product-visiontech-stat-value">KES {product.price}</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="improved-premium-product-visiontech-action-buttons">
                <button 
                  className="improved-premium-product-visiontech-learn-more-btn" 
                  onClick={toggleModal}
                >
                  <span className="improved-premium-product-visiontech-learn-more-text">Learn More</span>
                  <span className="improved-premium-product-visiontech-learn-more-icon">
                    <Info size={12} />
                  </span>
                </button>
                <button 
                  className="improved-premium-product-visiontech-invest-btn" 
                  onClick={() => onInvest(product)}
                >
                  <span className="improved-premium-product-visiontech-invest-text">Buy Now</span>
                  <span className="improved-premium-product-visiontech-invest-icon">
                    <Zap size={12} strokeWidth={3} />
                  </span>
                </button>
              </div>
            </div>
          </div>
          
          <div className="improved-premium-product-visiontech-countdown">
            <div className="improved-premium-product-visiontech-countdown-progress">
              <div className="improved-premium-product-visiontech-countdown-progress-inner"></div>
            </div>
            <div className="improved-premium-product-visiontech-countdown-text">
              Limited time offer: Save KES {savings} Today!
            </div>
          </div>
        </div>
      </div>

      {isModalOpen && (
        <div className="improved-premium-product-visiontech-modal-overlay" onClick={toggleModal}>
          <div className="improved-premium-product-visiontech-modal-content" onClick={e => e.stopPropagation()}>
            <button className="improved-premium-product-visiontech-modal-close" onClick={toggleModal}>
              <X size={18} />
            </button>
            
            <div className="improved-premium-product-visiontech-modal-header">
              <h2>VisionTech 2030 Power Pack Distribution</h2>
              <div className="improved-premium-product-visiontech-modal-discount-badge">
                <span>Limited Offer: {discountPercent}% OFF</span>
              </div>
            </div>

            <div className="improved-premium-product-visiontech-modal-body">
              <div className="improved-premium-product-visiontech-stage-selector">
                <button 
                  className={`improved-premium-product-visiontech-stage-button ${activeStage === 1 ? 'active' : ''}`}
                  onClick={() => setActiveStage(1)}
                >
                  Stage 1
                </button>
                <button 
                  className={`improved-premium-product-visiontech-stage-button ${activeStage === 2 ? 'active' : ''}`}
                  onClick={() => setActiveStage(2)}
                >
                  Stage 2
                </button>
                <button 
                  className={`improved-premium-product-visiontech-stage-button ${activeStage === 3 ? 'active' : ''}`}
                  onClick={() => setActiveStage(3)}
                >
                  Stage 3
                </button>
              </div>

              <div className="improved-premium-product-visiontech-video-section">
                {isVideoLoading && (
                  <div className="improved-premium-product-visiontech-video-loading">
                    <div className="improved-premium-product-visiontech-spinner"></div>
                    <p>Loading video content...</p>
                  </div>
                )}
                <video 
                  autoPlay 
                  loop 
                  muted 
                  className="improved-premium-product-visiontech-stage-video"
                  key={`video-${activeStage}`}
                  onLoadedData={handleVideoLoaded}
                  onCanPlay={handleVideoLoaded}
                >
                  <source src={`/videos/stage${activeStage}.mp4`} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>

              <div className="improved-premium-product-visiontech-content-section">
                <div className="improved-premium-product-visiontech-content-header">
                  <h3>VisionTech2030 Selected for Global Power Pack Distribution</h3>
                </div>
                
                <div className="improved-premium-product-visiontech-content-body">
                  <p>
                    VisionTech2030 has been chosen to distribute power pack packages across the United States, South America, and Europe, marking a significant milestone in the advancement of sustainable energy solutions.
                  </p>
                  
                  <div className="improved-premium-product-visiontech-feature-list">
                    <div className="improved-premium-product-visiontech-feature">
                      <div className="improved-premium-product-visiontech-feature-icon">✓</div>
                      <div className="improved-premium-product-visiontech-feature-text">
                        <strong>Enhancing Energy Independence:</strong> Reliable backup power during outages.
                      </div>
                    </div>
                    
                    <div className="improved-premium-product-visiontech-feature">
                      <div className="improved-premium-product-visiontech-feature-icon">✓</div>
                      <div className="improved-premium-product-visiontech-feature-text">
                        <strong>Reducing Carbon Footprint:</strong> Supporting the global transition to clean energy.
                      </div>
                    </div>
                    
                    <div className="improved-premium-product-visiontech-feature">
                      <div className="improved-premium-product-visiontech-feature-icon">✓</div>
                      <div className="improved-premium-product-visiontech-feature-text">
                        <strong>Optimizing Energy Usage:</strong> Efficient energy storage and management.
                      </div>
                    </div>
                  </div>
                  
                  <div className="improved-premium-product-visiontech-investment-highlight">
                    <div className="improved-premium-product-visiontech-investment-header">
                      <h4>Special Investment Opportunity</h4>
                      <div className="improved-premium-product-visiontech-investment-badge">Limited Time</div>
                    </div>
                    <div className="improved-premium-product-visiontech-investment-details">
                      <div className="improved-premium-product-visiontech-investment-row">
                        <span>Investment Amount:</span>
                        <div className="improved-premium-product-visiontech-price-tag">
                          <span className="improved-premium-product-visiontech-original">KES {originalPrice}</span>
                          <span className="improved-premium-product-visiontech-discounted">KES {product.price}</span>
                          <span className="improved-premium-product-visiontech-save-tag">Save {discountPercent}%</span>
                        </div>
                      </div>
                      
                      <div className="improved-premium-product-visiontech-investment-row">
                        <span>Event Duration:</span>
                        <span>Limited to 48 hours</span>
                      </div>
                      
                      <div className="improved-premium-product-visiontech-investment-row">
                        <span>Investment Period:</span>
                        <span>{product.cycle} days</span>
                      </div>
                      
                      <div className="improved-premium-product-visiontech-investment-row highlight">
                        <span>Potential Earnings:</span>
                        <span>KES {product.totalIncome}</span>
                      </div>
                      
                      <div className="improved-premium-product-visiontech-investment-row">
                        <span>Daily Earnings:</span>
                        <span>KES {product.dailyIncome} per day</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="improved-premium-product-visiontech-cta-section">
                    <div className="improved-premium-product-visiontech-cta-shine"></div>
                    <h4>Join the Future of Energy Today!</h4>
                    <p>Don't miss out on this exclusive opportunity to invest in VisionTech2030's cutting-edge energy solutions.</p>
                    <button 
                      className="improved-premium-product-visiontech-cta-button"
                      onClick={() => {
                        onInvest(product);
                        toggleModal();
                      }}
                    >
                      Invest Now
                      <span className="improved-premium-product-visiontech-button-shine"></span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .improved-premium-product-visiontech-container {
            position: relative;
            overflow: hidden;
            background: linear-gradient(135deg, #0f172a 0%, #1e1b4b 100%);
            border-radius: 16px;
            margin-bottom: 1.5rem;
            width: 100%;
            max-width: 450px;
            margin-bottom: 3rem;
            opacity: 1;
            transform: none;
            box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
            border: 1px solid rgba(120, 113, 255, 0.2);
          }
        
        .improved-premium-product-visiontech-container.visible {
          
        }
        
        .improved-premium-product-visiontech-particles {
          position: absolute;
          width: 100%;
          height: 100%;
          top: 0;
          left: 0;
          overflow: hidden;
          z-index: 0;
        }
        
        .improved-premium-product-visiontech-particle {
          position: absolute;
          border-radius: 50%;
          opacity: 0.4;
          background: radial-gradient(circle, rgba(121, 113, 234, 0.6) 0%, transparent 70%);
          animation-timing-function: ease-in-out;
          animation-iteration-count: infinite;
          animation-direction: alternate;
        }
        
        .improved-premium-product-visiontech-particle.p1 {
          width: 100px;
          height: 100px;
          top: -50px;
          right: 10%;
          animation: float1 8s infinite;
        }
        
        .improved-premium-product-visiontech-particle.p2 {
          width: 150px;
          height: 150px;
          bottom: -70px;
          right: -50px;
          animation: float2 12s infinite;
        }
        
        .improved-premium-product-visiontech-particle.p3 {
          width: 70px;
          height: 70px;
          bottom: 30%;
          left: -20px;
          animation: float3 10s infinite;
        }
        
        .improved-premium-product-visiontech-particle.p4 {
          width: 50px;
          height: 50px;
          top: 20%;
          left: 10%;
          animation: float4 6s infinite;
        }
        
        @keyframes float1 {
          0% { transform: translateY(0) scale(1); }
          100% { transform: translateY(-20px) scale(1.1); }
        }
        
        @keyframes float2 {
          0% { transform: translateY(0) scale(1); }
          100% { transform: translateY(20px) scale(1.15); }
        }
        
        @keyframes float3 {
          0% { transform: translateX(0) scale(1); }
          100% { transform: translateX(15px) scale(1.1); }
        }
        
        @keyframes float4 {
          0% { transform: translateY(0) translateX(0) scale(1); }
          100% { transform: translateY(-10px) translateX(10px) scale(1.05); }
        }
        
        .improved-premium-product-visiontech-content {
          position: relative;
          z-index: 1;
          padding: 16px;
        }
        
        .improved-premium-product-visiontech-header {
          margin-bottom: 12px;
        }
        
        .improved-premium-product-visiontech-badge-area {
          display: flex;
          gap: 8px;
          margin-bottom: 6px;
        }
        
        .improved-premium-product-visiontech-premium-badge {
          background: linear-gradient(135deg, #4f46e5, #7c3aed);
          padding: 4px 8px;
          border-radius: 20px;
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 10px;
          font-weight: 600;
          color: white;
          text-transform: uppercase;
          box-shadow: 0 2px 10px rgba(124, 58, 237, 0.3);
        }
        
        .improved-premium-product-visiontech-discount-badge {
          background: linear-gradient(135deg, #f59e0b, #d97706);
          padding: 4px 8px;
          border-radius: 20px;
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 10px;
          font-weight: 600;
          color: white;
          text-transform: uppercase;
          box-shadow: 0 2px 10px rgba(245, 158, 11, 0.3);
          transition: all 0.5s cubic-bezier(0.16, 1, 0.3, 1);
          transform: translateX(50px);
          opacity: 0;
        }
        
        .improved-premium-product-visiontech-discount-badge.show {
          transform: translateX(0);
          opacity: 1;
          animation: pulse 2s 1s infinite;
        }
        
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        
        .improved-premium-product-visiontech-title {
          font-size: 18px;
          font-weight: 700;
          margin: 0;
          background: linear-gradient(90deg, #a5b4fc, #e0e7ff);
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          letter-spacing: 0.5px;
        }
        
        .improved-premium-product-visiontech-main-area {
          display: flex;
          gap: 16px;
        }
        
        .improved-premium-product-visiontech-image-wrapper {
          width: 90px;
          height: 90px;
          flex-shrink: 0;
          border-radius: 12px;
          overflow: hidden;
          position: relative;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
          border: 1px solid rgba(120, 113, 255, 0.2);
        }
        
        .improved-premium-product-visiontech-image-glow {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: radial-gradient(circle at center, rgba(79, 70, 229, 0.2) 0%, transparent 70%);
          z-index: 1;
          animation: glowPulse 3s infinite;
        }
        
        @keyframes glowPulse {
          0%, 100% { opacity: 0.2; }
          50% { opacity: 0.4; }
        }
        
        .improved-premium-product-visiontech-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          z-index: 0;
          transition: transform 0.5s ease;
        }
        
        .improved-premium-product-visiontech-image-wrapper:hover .improved-premium-product-visiontech-image {
          transform: scale(1.05);
        }
        
        .improved-premium-product-visiontech-image-overlay {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          padding: 4px;
          background: linear-gradient(to top, rgba(14, 16, 45, 0.9), transparent);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 4px;
          font-size: 10px;
          color: #e0e7ff;
          z-index: 2;
        }
        
        .improved-premium-product-visiontech-details {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        
        .improved-premium-product-visiontech-stat-row {
          display: flex;
          justify-content: space-between;
          gap: 10px;
        }
        
        .improved-premium-product-visiontech-stat {
          display: flex;
          align-items: center;
          gap: 6px;
          width: calc(50% - 5px);
        }
        
        .improved-premium-product-visiontech-stat-icon {
          color: #a5b4fc;
        }
        
        .improved-premium-product-visiontech-stat-content {
          display: flex;
          flex-direction: column;
        }
        
        .improved-premium-product-visiontech-stat-label {
          font-size: 10px;
          color: #94a3b8;
        }
        
        .improved-premium-product-visiontech-stat-value {
          font-size: 12px;
          font-weight: 600;
          color: white;
        }
        
        .improved-premium-product-visiontech-stat-value.highlight {
          color: #f59e0b;
        }
        
        .improved-premium-product-visiontech-price-display {
          display: flex;
          align-items: center;
          gap: 5px;
        }
        
        .improved-premium-product-visiontech-original-price {
          font-size: 10px;
          color: #94a3b8;
          text-decoration: line-through;
        }
        
        .improved-premium-product-visiontech-action-buttons {
          display: flex;
          gap: 8px;
          margin-top: 5px;
        }
        
        /* Enhanced Learn More Button Styles */
        .improved-premium-product-visiontech-learn-more-btn {
          flex: 1;
          background: linear-gradient(135deg, #2d3748, #1a202c);
          color: white;
          border: 1px solid rgba(124, 58, 237, 0.3);
          border-radius: 8px;
          padding: 8px;
          font-size: 11px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 4px;
          position: relative;
          overflow: hidden;
          z-index: 1;
        }
        
        .improved-premium-product-visiontech-learn-more-btn::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(135deg, rgba(79, 70, 229, 0.2), transparent);
          z-index: -1;
          transition: opacity 0.3s ease;
          opacity: 0;
        }
        
        .improved-premium-product-visiontech-learn-more-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(79, 70, 229, 0.2);
          border-color: rgba(124, 58, 237, 0.5);
        }
        
        .improved-premium-product-visiontech-learn-more-btn:hover::before {
          opacity: 1;
        }
        
        .improved-premium-product-visiontech-learn-more-text {
          position: relative;
          transition: transform 0.3s ease;
        }
        
        .improved-premium-product-visiontech-learn-more-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          background: rgba(124, 58, 237, 0.3);
          width: 16px;
          height: 16px;
          transition: all 0.3s ease;
        }
        
        .improved-premium-product-visiontech-learn-more-btn:hover .improved-premium-product-visiontech-learn-more-text {
          transform: translateX(-2px);
        }
        
        .improved-premium-product-visiontech-learn-more-btn:hover .improved-premium-product-visiontech-learn-more-icon {
          transform: scale(1.1);
          background: rgba(124, 58, 237, 0.5);
        }
        
        /* Invest Button */
        .improved-premium-product-visiontech-invest-btn {
          flex: 1.2;
          background: linear-gradient(135deg, #f59e0b, #d97706);
          color: white;
          border: none;
          border-radius: 8px;
          padding: 8px;
          font-size: 11px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 3px 10px rgba(245, 158, 11, 0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 4px;
          position: relative;
          overflow: hidden;
        }
        
        .improved-premium-product-visiontech-invest-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(245, 158, 11, 0.4);
        }
        
        .improved-premium-product-visiontech-invest-text {
          position: relative;
          z-index: 2;
        }
        
        .improved-premium-product-visiontech-invest-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          z-index: 2;
          transition: transform 0.3s ease;
        }
        
        .improved-premium-product-visiontech-invest-btn:hover .improved-premium-product-visiontech-invest-icon {
          transform: scale(1.2);
          animation: pulse 1s infinite;
        }
        
        .improved-premium-product-visiontech-invest-btn::after {
          content: '';
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: linear-gradient(to right, transparent, rgba(255, 255, 255, 0.3), transparent);
          transform: rotate(30deg);
          animation: shimmer 3s infinite;
          pointer-events: none;
          z-index: 1;
        }
        
        @keyframes shimmer {
          0% { transform: translateX(-100%) rotate(30deg); }
          100% { transform: translateX(100%) rotate(30deg); }
        }
        
        .improved-premium-product-visiontech-countdown {
          margin-top: 12px;
          width: 100%;
        }
        
        .improved-premium-product-visiontech-countdown-progress {
          width: 100%;
          height: 6px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 3px;
          overflow: hidden;
        }
        
        .improved-premium-product-visiontech-countdown-progress-inner {
          height: 100%;
          width: 75%;
          background: linear-gradient(to right, #f59e0b, #d97706);
          border-radius: 3px;
          animation: countdown 30s linear infinite;
        }
        
        @keyframes countdown {
          0% { width: 90%; }
          100% { width: 5%; }
        }
        
        .improved-premium-product-visiontech-countdown-text {
          font-size: 10px;
          color: #f59e0b;
          text-align: center;
          margin-top: 6px;
          font-weight: 600;
        }
        
        /* Modal Styles */
        .improved-premium-product-visiontech-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.8);
          backdrop-filter: blur(8px);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
          padding: 16px;
        }
        
        .improved-premium-product-visiontech-modal-content {
          background: linear-gradient(135deg, #1e293b, #0f172a);
          border-radius: 16px;
          width: 100%;
          max-width: 800px;
          max-height: 90vh;
          overflow-y: auto;
          position: relative;
          border: 1px solid rgba(120, 113, 255, 0.2);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
          animation: modalFadeIn 0.3s cubic-bezier(0.16, 1, 0.3, 1);
        }
        
        @keyframes modalFadeIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        
        .improved-premium-product-visiontech-modal-close {
          position: absolute;
          top: 16px;
          right: 16px;
          background: rgba(255, 255, 255, 0.1);
          border: none;
          border-radius: 50%;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          cursor: pointer;
          transition: all 0.2s ease;
          z-index: 2;
        }
        
        .improved-premium-product-visiontech-modal-close:hover {
          background: rgba(255, 255, 255, 0.2);
          transform: rotate(90deg);
        }
        
        .improved-premium-product-visiontech-modal-header {
          padding: 24px 24px 16px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          position: relative;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        
        .improved-premium-product-visiontech-modal-header h2 {
          margin: 0;
          color: #e2e8f0;
          font-size: 20px;
          font-weight: 700;
        }
        
        .improved-premium-product-visiontech-modal-discount-badge {
          background: linear-gradient(135deg, #f59e0b, #d97706);
          color: white;
          font-size: 12px;
          font-weight: 600;
          padding: 4px 10px;
          border-radius: 20px;
          animation: pulseFade 2s infinite;
        }
        
        @keyframes pulseFade {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
        
        .improved-premium-product-visiontech-modal-body {
          padding: 16px 24px 24px;
        }
        
        .improved-premium-product-visiontech-stage-selector {
          display: flex;
          gap: 10px;
          margin-bottom: 16px;
        }
        
        .improved-premium-product-visiontech-stage-button {
          flex: 1;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          color: #e2e8f0;
          border-radius: 8px;
          padding: 8px 12px;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s ease;
          position: relative;
          overflow: hidden;
        }
        
        .improved-premium-product-visiontech-stage-button.active {
          background: linear-gradient(135deg, #f59e0b, #d97706);
          border-color: transparent;
          box-shadow: 0 2px 10px rgba(245, 158, 11, 0.3);
          color: white;
        }
        
        .improved-premium-product-visiontech-stage-button:hover:not(.active) {
          background: rgba(255, 255, 255, 0.1);
        }
        
        .improved-premium-product-visiontech-video-section {
          margin-bottom: 20px;
          border-radius: 12px;
          overflow: hidden;
          position: relative;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
          aspect-ratio: 16/9;
          border: 1px solid rgba(120, 113, 255, 0.2);
        }
        
        .improved-premium-product-visiontech-video-loading {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          background: rgba(15, 23, 42, 0.8);
          color: white;
          gap: 12px;
          font-size: 14px;
          z-index: 1;
          backdrop-filter: blur(4px);
        }
        
        .improved-premium-product-visiontech-spinner {
          width: 36px;
          height: 36px;
          border: 3px solid rgba(255, 255, 255, 0.1);
          border-top: 3px solid #f59e0b;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .improved-premium-product-visiontech-stage-video {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        
        .improved-premium-product-visiontech-content-section {
          color: #e2e8f0;
        }
        
        .improved-premium-product-visiontech-content-header {
          margin-bottom: 20px;
        }
        
        .improved-premium-product-visiontech-content-header h3 {
          color: #e2e8f0;
          font-size: 18px;
          font-weight: 700;
          margin: 0 0 12px;
          position: relative;
          display: inline-block;
        }
        
        .improved-premium-product-visiontech-content-header h3::after {
          content: '';
          position: absolute;
          bottom: -5px;
          left: 0;
          width: 40px;
          height: 2px;
          background: linear-gradient(to right, #f59e0b, transparent);
        }
        
        .improved-premium-product-visiontech-content-body {
          font-size: 14px;
          line-height: 1.6;
        }
        
        .improved-premium-product-visiontech-content-body p {
          margin-bottom: 16px;
          color: #94a3b8;
        }
        
        .improved-premium-product-visiontech-feature-list {
          margin: 20px 0;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        
        .improved-premium-product-visiontech-feature {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          transition: transform 0.2s ease;
        }
        
        .improved-premium-product-visiontech-feature:hover {
          transform: translateX(5px);
        }
        
        .improved-premium-product-visiontech-feature-icon {
          width: 22px;
          height: 22px;
          background: linear-gradient(135deg, #f59e0b, #d97706);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 12px;
          flex-shrink: 0;
        }
        
        .improved-premium-product-visiontech-feature-text {
          color: #94a3b8;
          font-size: 14px;
        }
        
        .improved-premium-product-visiontech-feature-text strong {
          color: #e2e8f0;
          font-weight: 600;
        }
        
        .improved-premium-product-visiontech-investment-highlight {
          background: rgba(15, 23, 42, 0.5);
          border: 1px solid rgba(120, 113, 255, 0.2);
          border-radius: 12px;
          padding: 16px;
          margin: 24px 0;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
        }
        
        .improved-premium-product-visiontech-investment-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }
        
        .improved-premium-product-visiontech-investment-header h4 {
          color: #e2e8f0;
          font-size: 16px;
          font-weight: 600;
          margin: 0;
        }
        
        .improved-premium-product-visiontech-investment-badge {
          background: linear-gradient(135deg, #f59e0b, #d97706);
          color: white;
          font-size: 10px;
          font-weight: 600;
          padding: 3px 8px;
          border-radius: 12px;
        }
        
        .improved-premium-product-visiontech-investment-details {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        
        .improved-premium-product-visiontech-investment-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 14px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.05);
          padding-bottom: 8px;
        }
        
        .improved-premium-product-visiontech-investment-row:last-child {
          border-bottom: none;
        }
        
        .improved-premium-product-visiontech-investment-row span:first-child {
          color: #94a3b8;
        }
        
        .improved-premium-product-visiontech-investment-row span:last-child {
          color: white;
          font-weight: 500;
        }
        
        .improved-premium-product-visiontech-investment-row.highlight {
          background: rgba(245, 158, 11, 0.1);
          padding: 10px;
          border-radius: 8px;
          margin: 8px 0;
          border: 1px solid rgba(245, 158, 11, 0.2);
        }
        
        .improved-premium-product-visiontech-investment-row.highlight span:last-child {
          color: #f59e0b;
          font-weight: 700;
          font-size: 16px;
        }
        
        .improved-premium-product-visiontech-price-tag {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .improved-premium-product-visiontech-original {
          color: #94a3b8;
          text-decoration: line-through;
          font-size: 13px;
        }
        
        .improved-premium-product-visiontech-discounted {
          color: white;
          font-weight: 700;
          font-size: 15px;
        }
        
        .improved-premium-product-visiontech-save-tag {
          background: linear-gradient(135deg, #f59e0b, #d97706);
          color: white;
          font-size: 10px;
          font-weight: 600;
          padding: 2px 6px;
          border-radius: 4px;
        }
        
        .improved-premium-product-visiontech-cta-section {
          margin-top: 24px;
          text-align: center;
          padding: 24px;
          background: linear-gradient(135deg, rgba(15, 23, 42, 0.8), rgba(30, 27, 75, 0.8));
          border-radius: 12px;
          border: 1px solid rgba(120, 113, 255, 0.2);
          position: relative;
          overflow: hidden;
        }
        
        .improved-premium-product-visiontech-cta-shine {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: radial-gradient(circle at top right, rgba(245, 158, 11, 0.1), transparent 70%);
          z-index: 0;
        }
        
        .improved-premium-product-visiontech-cta-section h4 {
          color: #e2e8f0;
          font-size: 18px;
          font-weight: 700;
          margin: 0 0 12px;
          position: relative;
          z-index: 1;
        }
        
        .improved-premium-product-visiontech-cta-section p {
          color: #94a3b8;
          font-size: 14px;
          margin-bottom: 20px;
          position: relative;
          z-index: 1;
        }
        
        .improved-premium-product-visiontech-cta-button {
          background: linear-gradient(135deg, #f59e0b, #d97706);
          color: white;
          border: none;
          border-radius: 8px;
          padding: 12px 24px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 2px 10px rgba(245, 158, 11, 0.3);
          position: relative;
          overflow: hidden;
          z-index: 1;
        }
        
        .improved-premium-product-visiontech-cta-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 15px rgba(245, 158, 11, 0.4);
        }
        
        .improved-premium-product-visiontech-button-shine {
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: linear-gradient(
            to right,
            rgba(255, 255, 255, 0) 0%,
            rgba(255, 255, 255, 0.3) 50%,
            rgba(255, 255, 255, 0) 100%
          );
          transform: rotate(30deg);
          animation: buttonShimmer 3s infinite;
        }
        
        @keyframes buttonShimmer {
          from { transform: translateX(-100%) rotate(30deg); }
          to { transform: translateX(100%) rotate(30deg); }
        }
        
        /* Responsive Styles */
        @media (max-width: 768px) {
          .improved-premium-product-visiontech-container {
            max-width: 100%;
          }
          
          .improved-premium-product-visiontech-modal-header,
          .improved-premium-product-visiontech-modal-body {
            padding: 16px;
          }
          
          .improved-premium-product-visiontech-stage-selector {
            flex-direction: column;
          }
          
          .improved-premium-product-visiontech-investment-row {
            flex-direction: column;
            align-items: flex-start;
            gap: 4px;
          }
          
          .improved-premium-product-visiontech-investment-row.highlight {
            flex-direction: row;
            align-items: center;
          }
          
          .improved-premium-product-visiontech-cta-section {
            padding: 16px;
          }
        }
        
        @media (max-width: 480px) {
          .improved-premium-product-visiontech-main-area {
            flex-direction: column;
            align-items: center;
          }
          
          .improved-premium-product-visiontech-image-wrapper {
            width: 120px;
            height: 120px;
            margin-bottom: 10px;
          }
          
          .improved-premium-product-visiontech-details {
            width: 100%;
          }
          
          .improved-premium-product-visiontech-modal-close {
            top: 10px;
            right: 10px;
          }
          
          .improved-premium-product-visiontech-modal-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 10px;
          }
          
          .improved-premium-product-visiontech-modal-discount-badge {
            align-self: flex-start;
          }
        }
      `}</style>
    </>
  );
};

export default ImprovedPremiumProductSection;